/* DAILE v2 (dv) — header search v2 (sections/dv-header.liquid).
 *
 * Owner request (2026-09-29): no separate search window. The magnifier turns
 * the glass pill itself into the search field; suggestions drop from a paper
 * panel under the pill.
 *
 * Module "dv-header-search" (DV registry, re-initialised by the theme editor):
 *   1. Search mode. [data-dv-search-open] (magnifier in the pill, "Search" row
 *      in the mobile menu) adds .dv-header--searching to the header: the pill's
 *      contents fade out (visibility, so dv-header.js's fit check is unaffected)
 *      and the absolutely positioned form fades in; the field is focused in the
 *      same click (iOS shows the keyboard only then). Escape, the close button
 *      and a pointer-down outside restore the header; focus returns to the
 *      magnifier (outside clicks on something focusable keep that focus).
 *      Focus leaving the form (Tab) also restores the header.
 *      Without JS the magnifier and the menu row are plain links to /search.
 *   2. Suggestions (ARIA 1.2 combobox + listbox). From 2 characters, 200 ms
 *      after the last keystroke, the section "dv-predictive-search" is fetched
 *      through the Section Rendering API:
 *        predictive — {predictive_search_url}?q=…&resources[type]=product,
 *          collection,article,page&resources[limit]=10&resources[limit_scope]=
 *          each&resources[options][unavailable_products]=last&section_id=…
 *        search — {search_url}?q=…&options[prefix]=last&type=product,article,
 *          page&section_id=… (Arabic: Predictive Search answers 417
 *          "Unsupported buyer locale"; the header starts in this mode on /ar,
 *          and any 417 switches to it for the rest of the visit).
 *      Stale requests are aborted, late answers for an older query ignored,
 *      answers cached per query. Arrow keys move aria-activedescendant through
 *      the options (wrapping), Enter opens the highlighted one or submits the
 *      form (q + options[prefix]=last; an empty query never leaves). The count
 *      ("6 results" / "Nothing found this time.", rendered by the section) is
 *      announced in a polite live region.
 */
(() => {
  'use strict';

  if (!window.DV || typeof window.DV.register !== 'function') return;

  const DV = window.DV;
  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 200;
  const CACHE_SIZE = 40;
  const PREDICTIVE_PARAMS = [
    'resources%5Btype%5D=product%2Ccollection%2Carticle%2Cpage',
    'resources%5Blimit%5D=10',
    'resources%5Blimit_scope%5D=each',
    'resources%5Boptions%5D%5Bunavailable_products%5D=last',
  ].join('&');
  const SEARCH_PARAMS = 'options%5Bprefix%5D=last&type=product%2Carticle%2Cpage';

  DV.register('dv-header-search', (root) => {
    const header = DV.queryAll(root, '[data-dv-header]')[0];
    const form = header ? header.querySelector('[data-dv-search-form]') : null;
    if (!form) return undefined;

    const input = form.querySelector('[data-dv-search-input]');
    const closeButton = form.querySelector('[data-dv-search-close]');
    const panel = form.querySelector('[data-dv-search-panel]');
    const listbox = form.querySelector('[data-dv-search-listbox]');
    const empty = form.querySelector('[data-dv-search-empty]');
    const status = form.querySelector('[data-dv-search-status]');
    if (!input || !panel || !listbox || !empty) return undefined;

    const triggers = DV.queryAll(header, '[data-dv-search-open]');
    const pillTrigger = triggers.find((el) => !el.closest('dialog')) || null;
    const sectionId = form.getAttribute('data-dv-search-section') || 'dv-predictive-search';
    const predictiveUrl = form.getAttribute('data-dv-predictive-url') || '/search/suggest';
    const searchUrl = form.getAttribute('data-dv-search-url') || form.getAttribute('action') || '/search';
    let mode = form.getAttribute('data-dv-search-mode') === 'search' ? 'search' : 'predictive';

    const cache = new Map();
    const cleanups = [];
    let isOpen = false;
    let timer = 0;
    let controller = null;
    let options = [];
    let activeIndex = -1;
    let announceFrame = 0;

    const on = (target, type, handler, opts) => {
      target.addEventListener(type, handler, opts);
      cleanups.push(() => target.removeEventListener(type, handler, opts));
    };

    /* ---------------------------------------------------------- listbox */

    const setActive = (index, { scroll = true } = {}) => {
      const previous = options[activeIndex];
      if (previous) previous.setAttribute('aria-selected', 'false');
      const option = options[index];
      if (!option) {
        activeIndex = -1;
        input.removeAttribute('aria-activedescendant');
        return;
      }
      activeIndex = index;
      option.setAttribute('aria-selected', 'true');
      input.setAttribute('aria-activedescendant', option.id);
      if (scroll) option.scrollIntoView({ block: 'nearest' });
    };

    const announce = (text) => {
      if (!status) return;
      if (announceFrame) cancelAnimationFrame(announceFrame);
      status.textContent = '';
      announceFrame = requestAnimationFrame(() => {
        announceFrame = 0;
        status.textContent = text;
      });
    };

    const hidePanel = () => {
      setActive(-1);
      panel.hidden = true;
      input.setAttribute('aria-expanded', 'false');
    };

    const clearResults = () => {
      hidePanel();
      options = [];
      listbox.replaceChildren();
      empty.replaceChildren();
      empty.hidden = true;
      if (status) status.textContent = '';
    };

    const render = (data, query) => {
      if (!isOpen || input.value.trim() !== query) return;
      form.classList.remove('is-loading');
      listbox.removeAttribute('aria-busy');
      setActive(-1);
      const node = document.importNode(data.node, true);
      if (data.count > 0) {
        empty.replaceChildren();
        empty.hidden = true;
        listbox.replaceChildren(node);
        listbox.hidden = false;
        options = [...listbox.querySelectorAll('[role="option"]')];
        input.setAttribute('aria-expanded', 'true');
      } else {
        options = [];
        listbox.replaceChildren();
        listbox.hidden = true;
        empty.replaceChildren(node);
        empty.hidden = false;
        input.setAttribute('aria-expanded', 'false');
      }
      panel.scrollTop = 0;
      panel.hidden = false;
      announce(data.status);
    };

    /* ---------------------------------------------------------- network */

    const cacheKey = (query) => `${mode}\u0000${query.toLowerCase()}`;

    const buildUrl = (query) => {
      const q = encodeURIComponent(query);
      const section = `section_id=${encodeURIComponent(sectionId)}`;
      return mode === 'predictive'
        ? `${predictiveUrl}?q=${q}&${PREDICTIVE_PARAMS}&${section}`
        : `${searchUrl}?q=${q}&${SEARCH_PARAMS}&${section}`;
    };

    const abort = () => {
      if (controller) controller.abort();
      controller = null;
    };

    const request = async (query) => {
      abort();
      const own = new AbortController();
      controller = own;
      const init = { signal: own.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } };
      let response = await fetch(buildUrl(query), init);
      if (response.status === 417 && mode === 'predictive') {
        // Predictive Search does not support this storefront language (e.g. Arabic).
        mode = 'search';
        response = await fetch(buildUrl(query), init);
      }
      if (!response.ok) throw new Error(`dv-header-search: HTTP ${response.status}`);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const node = doc.querySelector('[data-dv-ps-results]');
      if (!node) throw new Error('dv-header-search: no results fragment');
      const data = {
        node,
        count: Number.parseInt(node.getAttribute('data-dv-ps-count') || '0', 10) || 0,
        status: node.getAttribute('data-dv-ps-status') || '',
      };
      cache.set(cacheKey(query), data);
      if (cache.size > CACHE_SIZE) cache.delete(cache.keys().next().value);
      if (controller === own) controller = null;
      return data;
    };

    const onInput = () => {
      const query = input.value.trim();
      window.clearTimeout(timer);
      abort();
      setActive(-1);
      if (query.length < MIN_CHARS) {
        form.classList.remove('is-loading');
        clearResults();
        return;
      }
      const cached = cache.get(cacheKey(query));
      if (cached) {
        render(cached, query);
        return;
      }
      form.classList.add('is-loading');
      listbox.setAttribute('aria-busy', 'true');
      timer = window.setTimeout(() => {
        request(query)
          .then((data) => render(data, query))
          .catch((error) => {
            if (error && error.name === 'AbortError') return;
            if (input.value.trim() !== query) return;
            // Network or server trouble: no suggestions, Enter still searches.
            form.classList.remove('is-loading');
            listbox.removeAttribute('aria-busy');
          });
      }, DEBOUNCE_MS);
    };

    /* ------------------------------------------------------- open/close */

    const focusInput = () => {
      input.focus({ preventScroll: true });
      if (input.value) input.select();
    };

    let onOutside = null;

    const close = ({ restoreFocus = true } = {}) => {
      if (!isOpen) return;
      isOpen = false;
      window.clearTimeout(timer);
      abort();
      form.classList.remove('is-loading');
      listbox.removeAttribute('aria-busy');
      hidePanel();
      header.classList.remove('dv-header--searching', 'dv-header--search-instant');
      if (pillTrigger) pillTrigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('pointerdown', onOutside, true);
      // Removing the class shows the pill's contents at once (only hiding is delayed).
      if (restoreFocus && pillTrigger) pillTrigger.focus({ preventScroll: true });
      header.dispatchEvent(new CustomEvent('dv:header-search', { bubbles: true, detail: { open: false } }));
    };

    onOutside = (event) => {
      const target = event.target;
      if (!(target instanceof Node) || form.contains(target)) return;
      const focusable = target instanceof Element
        && target.closest('a[href], button, input, select, textarea, summary, label, [contenteditable], [tabindex]');
      close({ restoreFocus: false });
      // A click on empty space would move focus to <body> right after this event.
      if (!focusable && pillTrigger) window.setTimeout(() => pillTrigger.focus({ preventScroll: true }), 0);
    };

    const open = () => {
      if (isOpen) {
        focusInput();
        return;
      }
      isOpen = true;
      header.querySelectorAll('.dv-header__nav details[open]').forEach((details) => { details.open = false; });
      header.classList.add('dv-header--searching');
      if (pillTrigger) pillTrigger.setAttribute('aria-expanded', 'true');
      focusInput();
      document.addEventListener('pointerdown', onOutside, true);
      header.dispatchEvent(new CustomEvent('dv:header-search', { bubbles: true, detail: { open: true } }));
    };

    /* ----------------------------------------------------------- events */

    const onTriggerClick = (event) => {
      if (event.defaultPrevented) return;
      if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return; // new tab: plain link
      event.preventDefault();
      const dialog = event.currentTarget.closest('dialog');
      if (dialog && dialog.open) {
        // Mobile menu: the page is inert while the sheet is modal, so close it first. The pill switches
        // without transitions, which also leaves dv.js nothing visible to return focus to (the menu button),
        // and the field is focused in this same click.
        header.classList.add('dv-header--search-instant');
        dialog.addEventListener('dv:dialog:close', () => {
          requestAnimationFrame(() => header.classList.remove('dv-header--search-instant'));
        }, { once: true });
        dialog.close();
      }
      open();
    };

    const onTriggerKeydown = (event) => {
      if (event.key !== ' ') return;
      event.preventDefault();
      event.currentTarget.click();
    };

    triggers.forEach((trigger) => {
      trigger.setAttribute('role', 'button');
      on(trigger, 'click', onTriggerClick);
      on(trigger, 'keydown', onTriggerKeydown);
    });
    if (pillTrigger) pillTrigger.setAttribute('aria-expanded', 'false');

    on(input, 'input', onInput);

    on(input, 'keydown', (event) => {
      if (event.isComposing) return;
      const count = options.length;
      switch (event.key) {
        case 'ArrowDown':
        case 'ArrowUp': {
          if (!count) return;
          event.preventDefault();
          if (panel.hidden) {
            panel.hidden = false;
            input.setAttribute('aria-expanded', 'true');
          }
          const down = event.key === 'ArrowDown';
          let next;
          if (down) next = activeIndex + 1 >= count ? 0 : activeIndex + 1;
          else next = activeIndex <= 0 ? count - 1 : activeIndex - 1;
          setActive(next);
          break;
        }
        case 'Enter': {
          const option = options[activeIndex];
          if (!option || panel.hidden) return;
          event.preventDefault();
          option.click();
          break;
        }
        case 'Home':
        case 'End':
          setActive(-1);
          break;
        default:
      }
    });

    // Escape anywhere in the form (field or close button); also stops type=search from clearing the text.
    on(form, 'keydown', (event) => {
      if (event.key !== 'Escape' || !isOpen) return;
      event.preventDefault();
      event.stopPropagation();
      close({ restoreFocus: true });
    });

    on(form, 'focusout', (event) => {
      const next = event.relatedTarget;
      if (!isOpen || !(next instanceof Node) || form.contains(next)) return;
      close({ restoreFocus: false });
    });

    on(form, 'submit', (event) => {
      if (!input.value.trim()) {
        event.preventDefault();
        focusInput();
      }
    });

    if (closeButton) on(closeButton, 'click', () => close({ restoreFocus: true }));

    // Mouse follows the keyboard highlight, so there is only ever one.
    on(panel, 'pointermove', (event) => {
      if (event.pointerType !== 'mouse') return;
      const option = event.target instanceof Element ? event.target.closest('[role="option"]') : null;
      const index = option ? options.indexOf(option) : -1;
      if (index !== -1 && index !== activeIndex) setActive(index, { scroll: false });
    });

    // Keep focus in the field when an option or the panel is pressed (the click still navigates).
    on(panel, 'pointerdown', (event) => {
      if (event.target instanceof Element && event.target.closest('[role="option"]')) event.preventDefault();
    });

    return () => {
      window.clearTimeout(timer);
      abort();
      if (announceFrame) cancelAnimationFrame(announceFrame);
      close({ restoreFocus: false });
      document.removeEventListener('pointerdown', onOutside, true);
      cleanups.forEach((fn) => fn());
      triggers.forEach((trigger) => trigger.removeAttribute('role'));
      if (pillTrigger) pillTrigger.removeAttribute('aria-expanded');
      clearResults();
    };
  });
})();
